/**
 * One question per screen. Spec FR2, FR3, FR5.
 *
 * The three words are rendered verbatim from the frozen constants module, the
 * same strings the API validates against and the export emits. Nothing here
 * trims, cases, or otherwise touches them.
 *
 * There is no skip control. Next is disabled until MOST and LEAST are both set
 * and different, which the selection model makes structurally true. While it
 * is disabled it reads as waiting, not broken: a quiet outline with a hint
 * underneath, filling in the moment both choices are made.
 *
 * Back is offered by the parent under the one-step rule in navigation.ts.
 */

import { useEffect, useRef, useState } from 'react';
import { RESPONDENT_INSTRUCTION, TOTAL_QUESTIONS, type Question as Q, type Slot } from '@dna17q/shared';
import { EMPTY_SELECTION, canAdvance, roleOf, select, type Role, type Selection } from '../selection.ts';

interface Props {
  question: Q;
  /** Restored when navigating back within a session. Empty after a reload. */
  initial: Selection;
  answeredCount: number;
  isLast: boolean;
  canGoBack: boolean;
  busy: boolean;
  /** A short in-card message, for example after the server sent us back to a missed question. */
  notice: string | null;
  onBack: () => void;
  onNext: (selection: { most: Slot; least: Slot }, timeSpentMs: number) => void;
  onChange: (selection: Selection) => void;
}

const SLOTS: Slot[] = [1, 2, 3];

export function Question({
  question,
  initial,
  answeredCount,
  isLast,
  canGoBack,
  busy,
  notice,
  onBack,
  onNext,
  onChange,
}: Props) {
  const [selection, setSelection] = useState<Selection>(initial);
  const shownAt = useRef<number>(Date.now());
  const headingRef = useRef<HTMLHeadingElement>(null);

  /**
   * Mirrors `selection` so a tap always computes from the latest value.
   * Reading the state variable here would use the value captured at render, and
   * two taps in quick succession, MOST then LEAST, can both land before React
   * commits the first. That silently drops the MOST choice.
   */
  const selectionRef = useRef<Selection>(initial);

  // App gives each question its own key, so this component remounts per screen
  // and the initial state above is already correct. This is only for the timer
  // and for moving focus so a screen reader announces the new question.
  useEffect(() => {
    shownAt.current = Date.now();
    headingRef.current?.focus();
  }, [question.qsPosition]);

  function pick(role: Role, slot: Slot) {
    const next = select(selectionRef.current, role, slot);
    selectionRef.current = next;
    setSelection(next);
    onChange(next);
  }

  const ready = canAdvance(selection);
  const answeredForBar = Math.max(answeredCount, question.qsPosition - 1);
  const percent = Math.round((answeredForBar / TOTAL_QUESTIONS) * 100);

  return (
    <div className="card">
      <div className="progress">
        {/* The counter and the bar carry the position. No "n answered": it
            read as broken on question 1 with both choices made. */}
        <div className="progress-label">
          Question {question.qsPosition} of {TOTAL_QUESTIONS}
        </div>
        <div
          className="progress-track"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={TOTAL_QUESTIONS}
          aria-valuenow={answeredForBar}
          aria-label={`${answeredForBar} of ${TOTAL_QUESTIONS} questions answered`}
        >
          <div className="progress-fill" style={{ width: `${percent}%` }} />
        </div>
      </div>

      <h2 className="prompt" tabIndex={-1} ref={headingRef}>
        {RESPONDENT_INSTRUCTION}
      </h2>

      {notice ? (
        <p className="notice" role="alert">
          {notice}
        </p>
      ) : null}

      {/*
        Two radio groups over the same three words. Real radios, so arrow-key
        navigation and screen reader grouping come for free. The pills are
        self-labelling, so there is no column header row.
      */}
      <fieldset className="choices">
        <legend className="sr-only">Question {question.qsPosition}</legend>

        {SLOTS.map((slot) => {
          const word = question.words[slot - 1];
          const owner = roleOf(selection, slot);
          const mostId = `q${question.qsPosition}-most-${slot}`;
          const leastId = `q${question.qsPosition}-least-${slot}`;
          return (
            <div className={`choice-row${owner ? ` is-${owner}` : ''}`} key={slot}>
              <span className="choice-word">{word}</span>

              <div className="choice-pills">
                <div className="choice-cell">
                  <input
                    className="choice-input"
                    type="radio"
                    id={mostId}
                    name={`q${question.qsPosition}-most`}
                    aria-label={`${word} is most like me`}
                    checked={selection.most === slot}
                    onChange={() => pick('most', slot)}
                    disabled={busy}
                  />
                  <label className="choice-pill choice-pill-most" htmlFor={mostId}>
                    MOST
                  </label>
                </div>

                <div className="choice-cell">
                  <input
                    className="choice-input"
                    type="radio"
                    id={leastId}
                    name={`q${question.qsPosition}-least`}
                    aria-label={`${word} is least like me`}
                    checked={selection.least === slot}
                    onChange={() => pick('least', slot)}
                    disabled={busy}
                  />
                  <label className="choice-pill choice-pill-least" htmlFor={leastId}>
                    LEAST
                  </label>
                </div>
              </div>
            </div>
          );
        })}
      </fieldset>

      <div className="actions">
        <button
          className="btn btn-primary"
          type="button"
          disabled={!ready || busy}
          onClick={() => {
            const current = selectionRef.current;
            if (!canAdvance(current)) return;
            onNext(current, Date.now() - shownAt.current);
          }}
        >
          {busy ? 'Saving...' : isLast ? 'Finish' : 'Next'}
        </button>
        <p className="hint" aria-live="polite">
          {ready ? '' : 'Choose one MOST and one LEAST to continue.'}
        </p>

        {/*
          One step back, and only when the rule allows it. There is no skip
          control: Back only moves to a question that was already shown.
        */}
        {canGoBack ? (
          <button className="btn btn-quiet" type="button" onClick={onBack} disabled={busy}>
            Back
          </button>
        ) : null}
      </div>
    </div>
  );
}

export { EMPTY_SELECTION };
